// @ts-nocheck

import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import CustomStepConnector from '../CustomStepConnector/CustomStepConnector';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import './CustomStepper.css';
import { useSelector } from 'react-redux';

/**
 * CustomStepper component renders a stepper with clickable steps.
 *
 * @param {string} id - The id for the stepper container.
 * @param {Array} steps - An array of step objects containing `active`, `id`, `key`, `shouldRender`, and `title`.
 * @param {function} onStepClick - Callback function when a step is clicked.
 * @param {object} rest - Any additional props to be passed to the stepper container.
 */

const CustomStepper = ({ id, steps, onStepClick, ...rest }) => {
  const activeIndex = steps.findIndex((step) => step.active);
  const active = activeIndex !== -1 ? activeIndex : -1;

  const color = useSelector((state) => state?.color || {});
  const primaryColor = color?.highlightColor || 'var(--primary-600, #1361d8)';

  return (
    <Box
      id={id}
      sx={{
        width: 'fit-content',
        maxWidth: '600px',
        marginBottom: '24px'
      }}
      className='book-now__stepper'
      data-testid='book-now-stepper'
      {...rest}
    >
      <Stepper
        className='book-now__stepper-content'
        activeStep={active}
        alternativeLabel={false}
        orientation='horizontal'
        connector={<CustomStepConnector />}
        sx={{
          '& .MuiStepConnector-root': {
            top: '50%',
            left: 'calc(-50% + 20px)',
            right: 'calc(50% + 20px)',
            transform: 'translateY(-50%)'
          },
          '& .MuiStepConnector-line': {
            borderTopWidth: 2,
            borderRadius: 1,
            minHeight: '2px'
          },
          '& .MuiStep-root': {
            padding: '0 16px',
            minWidth: 'auto',
            '&:first-of-type': {
              paddingLeft: 0
            },
            '&:last-of-type': {
              paddingRight: 0
            }
          },
          '& .MuiStepLabel-root': {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            cursor: 'pointer'
          },
          '& .MuiStepLabel-iconContainer': {
            paddingRight: '12px',
            marginBottom: 0
          },
          '& .MuiStepLabel-labelContainer': {
            marginLeft: 0,
            marginTop: 0
          },
          // Add dynamic primaryColor styling for active step
          '& .MuiSvgIcon-root.MuiStepIcon-root.Mui-active': {
            fill: `${primaryColor} !important`
          },
          '& .MuiSvgIcon-root.MuiStepIcon-text': {
            color: `${primaryColor} !important`
          },

          // Add dynamic primaryColor styling for completed step
          '& .MuiSvgIcon-root.MuiStepIcon-root.Mui-completed': {
            fill: `#10b981 !important`
          },
          // Also style the active step label
          '& .MuiStep-root.Mui-completed .MuiStepLabel-label': {
            color: primaryColor
          }
        }}
      >
        {steps.map((step, index) => {
          const clickable = index <= active + 1;
          const isCompleted = index < active;
          const isActive = index === active;

          return (
            <Step
              key={step?.title}
              className={` 
                ${clickable ? 'clickable-step' : 'inactive-step'}
                ${isCompleted ? 'completed-step' : ''}
                ${isActive ? 'active-step' : ''}
              `.trim()}
              onClick={(event) => clickable && onStepClick(event, index)}
              completed={isCompleted}
            >
              <StepLabel
                className={clickable ? 'next-step' : ''}
                StepIconProps={{
                  className: `
                    ${clickable ? 'next-step-icon' : ''}
                    ${isCompleted ? 'completed-step-icon' : ''}
                    ${isActive ? 'active-step-icon' : ''}
                  `.trim(), // Add dynamic styling for active icon
                  ...(isActive && {
                    style: {
                      color: primaryColor,
                      backgroundColor: primaryColor
                    }
                  })
                }}
              >
                {step?.title}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

CustomStepper.propTypes = {
  id: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      active: PropTypes.bool,
      id: PropTypes.number,
      key: PropTypes.string,
      shouldRender: PropTypes.bool,
      title: PropTypes.string
    })
  ).isRequired,
  onStepClick: PropTypes.func.isRequired
};

export default CustomStepper;
